import type { Map as MapLibreMap } from 'maplibre-gl';
import { TrackManager } from './TrackManager';

export type MapStyleType = 'osm' | 'topo' | 'satellite' | 'streets';

interface StyleConfig {
  version: number;
  sources: any;
  layers: any[];
}

export class MapStyleManager {
  private map: MapLibreMap | null = null;
  private trackManager: TrackManager | null = null;
  private currentStyle: MapStyleType = 'osm';
  private tileServerUrl: string;
  private isTransitioning: boolean = false;

  constructor(tileServerUrl: string) {
    this.tileServerUrl = tileServerUrl;
  }

  setMap(map: MapLibreMap | null) {
    this.map = map;
  }

  setTrackManager(trackManager: TrackManager | null) {
    this.trackManager = trackManager;
  }

  getCurrentStyle(): MapStyleType {
    return this.currentStyle;
  }

  async switchToStyle(style: MapStyleType): Promise<void> {
    if (!this.map) {
      // No map available
      return;
    }

    if (this.isTransitioning) {
      // Style transition in progress
      return;
    }

    if (style === this.currentStyle) {
      // Already using this style
      return;
    }

    // Switching map style
    this.isTransitioning = true;
    this.currentStyle = style;

    try {
      // Get the appropriate style configuration
      const styleConfig = await this.getStyleConfig(style);

      // Apply the new style
      this.map.setStyle(styleConfig);

      // Wait for style to load and restore tracks
      await this.waitForStyleLoadAndRestoreTracks();

      // Style switched successfully
    } catch (error) {
      console.error('Error switching style:', error);
    } finally {
      this.isTransitioning = false;
    }
  }

  private async getStyleConfig(style: MapStyleType): Promise<any> {
    switch (style) {
      case 'satellite':
        return this.getSatelliteStyle();
      case 'topo':
        return this.getTopoStyle();
      case 'streets':
        return this.getStreetsStyle();
      case 'osm':
      default:
        return this.getOSMStyle();
    }
  }

  private async getSatelliteStyle(): Promise<any> {
    // Try local satellite tiles first
    const satelliteUrl = `${this.tileServerUrl}/styles/satellite/style.json`;

    try {
      const response = await fetch(satelliteUrl);
      if (response.ok) {
        // Using local satellite style
        return satelliteUrl;
      }
    } catch (error) {
      // Using fallback satellite style
    }

    // Fallback to ESRI satellite tiles
    return {
      version: 8,
      sources: {
        'satellite-tiles': {
          type: 'raster',
          tiles: [
            'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
          ],
          tileSize: 256,
          attribution: '© Esri'
        }
      },
      layers: [{
        id: 'satellite-tiles-layer',
        type: 'raster',
        source: 'satellite-tiles',
        minzoom: 0,
        maxzoom: 19
      }]
    };
  }

  private getTopoStyle(): StyleConfig {
    return {
      version: 8,
      sources: {
        'topo-tiles': {
          type: 'raster',
          tiles: [
            'https://a.tile.opentopomap.org/{z}/{x}/{y}.png',
            'https://b.tile.opentopomap.org/{z}/{x}/{y}.png',
            'https://c.tile.opentopomap.org/{z}/{x}/{y}.png'
          ],
          tileSize: 256,
          attribution: '© OpenTopoMap (CC-BY-SA)'
        }
      },
      layers: [{
        id: 'topo-tiles-layer',
        type: 'raster',
        source: 'topo-tiles',
        minzoom: 0,
        maxzoom: 17
      }]
    };
  }

  private getStreetsStyle(): StyleConfig {
    return {
      version: 8,
      sources: {
        'street-tiles': {
          type: 'raster',
          tiles: [
            'https://a.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
            'https://b.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
            'https://c.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png'
          ],
          tileSize: 256,
          attribution: '© OpenStreetMap © CartoDB'
        }
      },
      layers: [{
        id: 'street-tiles-layer',
        type: 'raster',
        source: 'street-tiles',
        minzoom: 0,
        maxzoom: 19
      }]
    };
  }

  private getOSMStyle(): StyleConfig {
    return {
      version: 8,
      sources: {
        'osm-tiles': {
          type: 'raster',
          tiles: [
            'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
            'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
            'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png'
          ],
          tileSize: 256,
          attribution: '© OpenStreetMap contributors'
        }
      },
      layers: [{
        id: 'osm-tiles-layer',
        type: 'raster',
        source: 'osm-tiles',
        minzoom: 0,
        maxzoom: 19
      }]
    };
  }

  private waitForStyleLoadAndRestoreTracks(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.map) {
        resolve();
        return;
      }

      const handleStyleLoad = () => {
        // Style loaded, restoring tracks

        // Ensure tracks are restored
        if (this.trackManager) {
          // Triggering TrackManager restoration
          // Give the style a moment to fully settle
          setTimeout(() => {
            this.trackManager?.onStyleChange();
            resolve();
          }, 100);
        } else {
          // No TrackManager available
          resolve();
        }
      };

      // Listen for style load event
      this.map.once('style.load', handleStyleLoad);

      // Fallback timeout in case style.load doesn't fire
      setTimeout(() => {
        // Style load timeout
        if (this.map?.isStyleLoaded()) {
          handleStyleLoad();
        } else {
          resolve();
        }
      }, 2000);
    });
  }

  // Check if we're currently transitioning
  isInTransition(): boolean {
    return this.isTransitioning;
  }
}